import { Toggle as TogglePrimitive } from '@base-ui/react/toggle';
import { cva } from 'class-variance-authority';
import { type ReactNode, forwardRef } from 'react';

import type { ButtonSize } from '@/components/buttons/Button';
import { Spinner } from '@/components/loaders';
import { cn } from '@/lib/utils';

/* eslint-disable lingui/no-unlocalized-strings */

const pseudoElementBase = ['transition-all duration-200 ease-in-out'];

const ghostBase = [
  'border border-transparent',
  'transition-colors duration-150',
  'focus-visible:border-1',
];

export type ToggleVariant = 'secondary' | 'ghost';
export type ToggleSize = ButtonSize;
export type ToggleShape = 'round' | 'square';

export const toggleVariants = cva(
  [
    'relative inline-flex items-center justify-center',
    'font-semibold font-sans whitespace-nowrap',
    'cursor-pointer disabled:cursor-not-allowed aria-disabled:cursor-not-allowed',
    'outline-none shrink-0',
  ],
  {
    variants: {
      variant: {
        secondary: [
          ...pseudoElementBase,
          'text-gray-900 bg-white border-gray-900',
          'focus-visible:ring-[3px] focus-visible:ring-gray-900/30 focus-visible:outline-1 focus-visible:outline-gray-900/30',
          'hover:bg-gray-300',
          'active:bg-gray-400',
          'data-pressed:text-green-500 data-pressed:bg-gray-900',
          'disabled:text-gray-500 disabled:bg-gray-200 disabled:border-gray-200',
          'aria-disabled:text-gray-500 aria-disabled:bg-gray-200 aria-disabled:border-gray-200',
        ],
        ghost: [
          ...ghostBase,
          'text-gray-900 bg-transparent',
          'focus-visible:bg-black/10',
          'hover:bg-black/10',
          'active:bg-black/20',
          'data-pressed:bg-black/20',
          'disabled:text-gray-400 disabled:bg-transparent disabled:border-transparent',
          'aria-disabled:text-gray-400 aria-disabled:bg-transparent aria-disabled:border-transparent',
        ],
      },
      size: {
        small: ['h-6 text-xs leading-none gap-1', '[&_svg]:size-3'],
        medium: ['h-8 text-xs leading-none gap-1.5', '[&_svg]:size-4'],
        large: ['h-10 text-base leading-none gap-1.5', '[&_svg]:size-5'],
        'icon-small': ['size-6 text-xs leading-none', '[&_svg]:size-4'],
        icon: ['size-8 text-xs leading-none', '[&_svg]:size-5'],
        'icon-large': ['size-10 text-base leading-none', '[&_svg]:size-6'],
      },
      shape: {
        round: 'rounded-full',
        square: 'rounded-xs',
      },
    },
    defaultVariants: {
      variant: 'ghost',
      size: 'medium',
      shape: 'round',
    },
  },
);

const PADDING_FILLED_ROUND_BY_BUTTON_SIZE: Record<ToggleSize, string> = {
  small: 'px-3',
  medium: 'px-4',
  large: 'px-5',
  'icon-small': 'p-0',
  icon: 'p-0',
  'icon-large': 'p-0',
};
const PADDING_FILLED_SQUARE_BY_BUTTON_SIZE: Record<ToggleSize, string> = {
  small: 'px-1.5',
  medium: 'px-2.5',
  large: 'px-3.5',
  'icon-small': 'p-0',
  icon: 'p-0',
  'icon-large': 'p-0',
};
const PADDING_GHOST_BY_BUTTON_SIZE: Record<ToggleSize, string> = {
  small: 'px-1.5',
  medium: 'px-2.5',
  large: 'px-3.5',
  'icon-small': 'p-0',
  icon: 'p-0',
  'icon-large': 'p-0',
};
const LOADING_SIZE_BY_BUTTON_SIZE: Record<ToggleSize, 'xs' | 'sm' | 'md'> = {
  small: 'xs',
  medium: 'sm',
  large: 'md',
  'icon-small': 'xs',
  icon: 'sm',
  'icon-large': 'md',
};

export type ToggleProps = TogglePrimitive.Props & {
  variant?: ToggleVariant;
  size?: ToggleSize;
  shape?: ToggleShape;
  isLoading?: boolean;
};

type ToggleClassNameArgs = Pick<ToggleProps, 'variant' | 'size' | 'shape' | 'className'>;

export const getToggleClassName = ({
  variant = 'ghost',
  size = 'medium',
  shape = 'round',
  className,
}: ToggleClassNameArgs) => {
  const isGhost = variant === 'ghost';
  const paddingClass = isGhost
    ? PADDING_GHOST_BY_BUTTON_SIZE[size]
    : shape === 'square'
      ? PADDING_FILLED_SQUARE_BY_BUTTON_SIZE[size]
      : PADDING_FILLED_ROUND_BY_BUTTON_SIZE[size];
  const focusBorderClass = !isGhost ? 'border-1' : undefined;

  return {
    className: cn(
      toggleVariants({ variant, size, shape }),
      paddingClass,
      focusBorderClass,
      className,
    ),
    loadingSize: LOADING_SIZE_BY_BUTTON_SIZE[size],
  };
};

type ToggleContentProps = {
  children?: ReactNode;
  isLoading?: boolean;
  loadingSize: 'xs' | 'sm' | 'md';
};

const renderToggleContent = ({ children, isLoading = false, loadingSize }: ToggleContentProps) => (
  <>
    {isLoading && (
      <span className="absolute top-1/2 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2">
        <Spinner variant="spoke" size={loadingSize} className="text-inherit" />
      </span>
    )}
    {children && (
      <span
        className={cn('relative inline-flex items-center gap-[inherit]', isLoading && 'invisible')}
      >
        {children}
      </span>
    )}
  </>
);

const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      className,
      children,
      variant = 'ghost',
      size = 'medium',
      shape = 'round',
      type = 'button',
      isLoading = false,
      disabled = false,
      onClick,
      ...rest
    },
    ref,
  ) => {
    const { className: computedClassName, loadingSize } = getToggleClassName({
      variant,
      size,
      shape,
      className,
    });

    const handleClick: ToggleProps['onClick'] = (event) => {
      if (disabled || isLoading) {
        event.stopPropagation();
        event.preventDefault();
        return;
      }
      onClick?.(event);
    };

    return (
      <TogglePrimitive
        data-slot="toggle"
        ref={ref}
        type={type}
        disabled={disabled}
        aria-busy={isLoading || undefined}
        className={computedClassName}
        onClick={handleClick}
        {...rest}
      >
        {renderToggleContent({ children, isLoading, loadingSize })}
      </TogglePrimitive>
    );
  },
);

Toggle.displayName = 'Toggle';

export default Toggle;
